import { useTranslation } from "react-i18next";
import type { IWidgetRenderContext } from "@/components/dashboard-grid";
import { useGetHeadCountQuery } from "@/services/(company)/userApi";
import { useGetCompanyTimeAttendanceStatsQuery } from "@/services/(company)/api.time-attendance";
import { cn } from "@/utils/cn";
import {
  CompositionBar,
  DeltaPill,
  MetricValue,
  MiniMetric,
  MiniStat,
  WidgetNoAccess,
} from "./primitives";

/**
 * Headcount, and at every size but the two most compact, how that headcount is
 * actually showing up today. The composition data is skipped at `xs` and `sm` —
 * neither has anywhere to put it, so neither should pay for it.
 *
 * Two independent permission surfaces, so two different fallbacks. The headcount
 * itself is this widget's own gate (`EMPLOYEES`); losing it leaves nothing to
 * show, so the whole body becomes the no-access state. The breakdown comes from
 * time-attendance, a module the registry never checked to admit this widget —
 * losing only that keeps the headline and replaces just the breakdown, which is
 * the honest reading and better than three zeros.
 */
export const HeadcountWidget = ({ size }: IWidgetRenderContext) => {
  const { t } = useTranslation(["common", "company", "nav"]);
  const { data, isLoading, error } = useGetHeadCountQuery();
  const {
    data: attendance,
    isLoading: isAttendanceLoading,
    error: attendanceError,
  } = useGetCompanyTimeAttendanceStatsQuery(
    {},
    // Neither compact size shows the composition data, so neither should pay
    // for it. `tall` keeps `xs`'s width but not its content — it does want it.
    { skip: size === "sm" || size === "xs" },
  );

  if (error) return <WidgetNoAccess error={error} />;

  const noAttendance = Boolean(attendanceError);
  const total = data?.data?.totalEmployees ?? 0;
  const change = data?.data?.monthOverMonthChangePercent ?? null;
  const stats = attendance?.data;

  // The card draws the chip and the title at every size, this one included.
  // The month-over-month figure rides alongside as the accessory:
  // the label is dropped because "vs last month" has nowhere to go in 140px,
  // and a bare signed percentage next to a headcount is unambiguous without it.
  if (size === "xs") {
    return (
      <MiniMetric
        value={total}
        isLoading={isLoading}
        tone="info"
        trailing={<DeltaPill percent={change} />}
      />
    );
  }

  if (size === "sm") {
    return (
      <div className="flex h-full flex-col justify-center gap-1">
        <MetricValue value={total} isLoading={isLoading} tone="info" />
        <DeltaPill
          percent={change}
          label={t("company:widgets.vs_last_month")}
        />
      </div>
    );
  }

  // Same width as `sm`, twice the height — the number stays the size it was
  // there, and the extra row goes to the breakdown `md` shows sideways instead.
  if (size === "tall") {
    return (
      <div
        className={cn(
          "flex h-full flex-col gap-2",
          // `justify-between` spreads whatever height the second block does not
          // use into the gap above it — three stat rows fill enough of a `tall`
          // card that this is invisible with real data. The no-access line is
          // one row, and letting `justify-between` distribute the freed height
          // dumped it as a bare gap between the headline and a divider stranded
          // near the bottom, rather than the two blocks sitting together.
          noAttendance ? "justify-start" : "justify-between",
        )}
      >
        <div className="flex flex-col gap-1">
          <MetricValue value={total} isLoading={isLoading} tone="info" />
          <DeltaPill
            percent={change}
            label={t("company:widgets.vs_last_month")}
          />
        </div>
        <div className="flex flex-col gap-1.5 border-t border-border pt-2">
          {noAttendance ? (
            <WidgetNoAccess
              error={attendanceError}
              message={t("company:widgets.no_access_to_attendance")}
            />
          ) : (
            <>
              <MiniStat
                label={t("company:dashboard.present")}
                tone="success"
                value={stats?.totalPresent ?? 0}
                isLoading={isAttendanceLoading}
              />
              <MiniStat
                label={t("company:dashboard.on_leave")}
                tone="info"
                value={stats?.totalOnLeave ?? 0}
                isLoading={isAttendanceLoading}
              />
              <MiniStat
                label={t("company:dashboard.absent")}
                tone="danger"
                value={stats?.totalAbsent ?? 0}
                isLoading={isAttendanceLoading}
              />
            </>
          )}
        </div>
      </div>
    );
  }

  // Wide and one row tall — there is width for the breakdown beside the
  // headline but no height to stack it underneath, so it rides alongside
  // instead. The composition bar is `lg`-only: it is decoration on top of
  // figures the `MiniStat`s already say, and a one-row card has no height to
  // spend on decoration.
  if (size === "md") {
    return (
      <div className="flex h-full items-center gap-4">
        <div className="min-w-0 shrink-0">
          <MetricValue
            value={total}
            isLoading={isLoading}
            tone="info"
            scale="lg"
          />
          <DeltaPill
            percent={change}
            label={t("company:widgets.vs_last_month")}
          />
        </div>
        {noAttendance ? (
          <WidgetNoAccess
            error={attendanceError}
            message={t("company:widgets.no_access_to_attendance")}
          />
        ) : (
          <div className="grid min-w-0 flex-1 grid-cols-3 gap-2 border-s border-border ps-4">
            <MiniStat
              label={t("company:dashboard.present")}
              tone="success"
              value={stats?.totalPresent ?? 0}
              isLoading={isAttendanceLoading}
            />
            <MiniStat
              label={t("company:dashboard.on_leave")}
              tone="info"
              value={stats?.totalOnLeave ?? 0}
              isLoading={isAttendanceLoading}
            />
            <MiniStat
              label={t("company:dashboard.absent")}
              tone="danger"
              value={stats?.totalAbsent ?? 0}
              isLoading={isAttendanceLoading}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col justify-between gap-2">
      <div className="flex items-end gap-2">
        <MetricValue
          value={total}
          isLoading={isLoading}
          tone="info"
          scale="xl"
        />
        <span className="pb-1">
          <DeltaPill
            percent={change}
            label={t("company:widgets.vs_last_month")}
          />
        </span>
      </div>

      {/* The bar is deliberately dropped rather than drawn empty: with every
          segment at zero it normalises against a zero sum and reads as a full
          neutral band, which looks like data. */}
      {noAttendance ? (
        <WidgetNoAccess
          message={t("company:widgets.no_access_to_attendance")}
        />
      ) : (
        <div>
          <CompositionBar
            isLoading={isAttendanceLoading}
            segments={[
              {
                label: t("company:dashboard.present"),
                value: stats?.totalPresent ?? 0,
                tone: "success",
              },
              {
                label: t("company:dashboard.on_leave"),
                value: stats?.totalOnLeave ?? 0,
                tone: "info",
              },
              {
                label: t("company:dashboard.absent"),
                value: stats?.totalAbsent ?? 0,
                tone: "danger",
              },
            ]}
          />
          <div className="mt-2 grid grid-cols-3 gap-2">
            <MiniStat
              label={t("company:dashboard.present")}
              tone="success"
              value={stats?.totalPresent ?? 0}
              isLoading={isAttendanceLoading}
            />
            <MiniStat
              label={t("company:dashboard.on_leave")}
              tone="info"
              value={stats?.totalOnLeave ?? 0}
              isLoading={isAttendanceLoading}
            />
            <MiniStat
              label={t("company:dashboard.absent")}
              tone="danger"
              value={stats?.totalAbsent ?? 0}
              isLoading={isAttendanceLoading}
            />
          </div>
        </div>
      )}

      {/* Reachable at `lg` only — `xs` through `md` all return above. */}
      {!noAttendance && (
        <p className="text-[11px] text-muted-foreground">
          {t("company:widgets.measured_against_attendance")}
        </p>
      )}
    </div>
  );
};
